import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { axiosClient, getBaseUrlVersioned } from "../../services/axiosClient";
import axios from "axios";
import { localStorageService } from "../../services/index";
import { API_VERSION } from "../types";
import { FeatureFlags, UserFlow } from "./types";
import { updateDefaultPersona } from "./userPersona/personaSlice";
import { setShowUnsavedProfileChangesWarning } from "./userSettings/userSettingsSlice";
const initialState = {
    externalUserId: null,
    isInvalid: false,
    authUser: null,
    profile: null,
    isProfileModified: false,
    guestUserId: null,
    permissions: [],
    roles: [],
    isAuthenticated: false,
    isProfileLoaded: false,
    isAuthLoaded: false,
    tapTrackingId: "",
    isLoading: false,
    error: null,
    isSuccess: false,
    loadingType: null,
    features: {
        [FeatureFlags.TTS]: false,
        [FeatureFlags.V2]: false,
    },
};
const API_PATH_USERMODERATION = "users/moderation";
const API_PATH_CHECKOUT_NEW_ORDER = "checkout/new_order";
/**
 * Thunk action to get user moderation .
 * Makes an HTTP GET request to fetch the user moderation .
 */
export const getUserModeration = createAsyncThunk("user/getModeration", async () => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/${API_PATH_USERMODERATION}`;
        const response = await axiosClient.get(apiUrl);
        return response.data;
    }
    catch (error) {
        throw error;
    }
});
export const updateUserProfile = createAsyncThunk("user/updateProfile", async (payload, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/users`;
        const response = await axiosClient.patch(apiUrl, payload);
        thunkAPI.dispatch(setShowUnsavedProfileChangesWarning(false));
        // Update default persona in personas state directly from the response data
        const { default_persona_id: id, name, avatar_url, highlights, } = response.data;
        thunkAPI.dispatch(updateDefaultPersona({ id, name, avatar_url, highlights }));
        return response.data;
    }
    catch (error) {
        if (axios.isAxiosError(error) && error?.response?.data) {
            const errorData = error?.response?.data || {};
            const errorCode = error?.response?.data?.errorCode;
            errorData.name = error.name;
            errorData.code = errorCode;
            errorData.stack = error.stack;
            throw thunkAPI.rejectWithValue(errorData);
        }
        throw error;
    }
});
export const createSubscriptionOrder = createAsyncThunk("user/createSubscriptionOrder", async (params, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/${API_PATH_CHECKOUT_NEW_ORDER}`;
        const response = await axiosClient.post(apiUrl, params);
        return response.data;
    }
    catch (err) {
        throw thunkAPI.rejectWithValue(err?.response?.data);
    }
});
const handleLoading = (state, loadingType = null) => {
    state.isLoading = !!loadingType;
    state.loadingType = loadingType;
};
export const userSlice = createSlice({
    name: "user",
    initialState,
    reducers: {
        setProfile: (state, action) => {
            state.profile = action.payload;
            state.isProfileLoaded = true;
        },
        setGuestUserId: (state, action) => {
            state.guestUserId = action.payload;
            localStorageService.setGuestUserId(action.payload);
        },
        updateProfile: (state, action) => {
            state.profile = {
                ...state.profile,
                ...action.payload,
            };
            state.isProfileLoaded = true;
        },
        resetUserProfile: (state) => {
            state.profile = null;
            state.isProfileLoaded = false;
        },
        setIsProfileModified: (state, action) => {
            state.isProfileModified = action.payload;
        },
        setAuthUser: (state, action) => {
            state.authUser = action.payload;
            localStorageService.setUserId(action.payload.id);
        },
        resetAuthUser: (state) => {
            state.authUser = null;
        },
        setUserPermissions: (state, action) => {
            state.permissions = action.payload;
        },
        setUserRoles: (state, action) => {
            state.roles = action.payload;
        },
        setExternalUserId: (state, action) => {
            state.externalUserId = action.payload;
        },
        setInvalid: (state, action) => {
            state.isInvalid = action.payload;
        },
        setIsUserAuthenticated: (state, action) => {
            state.isAuthenticated = action.payload;
        },
        setIsUserAuthLoaded: (state, action) => {
            state.isAuthLoaded = action.payload;
        },
        setUserTapTrackingId: (state, action) => {
            state.tapTrackingId = action.payload;
        },
        setIsLoading: (state, action) => {
            state.isLoading = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
        setIsSucceess: (state, action) => {
            state.isSuccess = action.payload;
        },
        setFeatureFlags: (state, action) => {
            state.features = action.payload;
        },
        setAuthenticationStatus: (state, action) => {
            const { authLoaded, isAuthenticated } = action.payload;
            state.isAuthLoaded = authLoaded;
            state.isAuthenticated = isAuthenticated;
        },
    },
    extraReducers(builder) {
        builder.addCase(updateUserProfile.pending, (state) => {
            handleLoading(state, UserFlow.UPDATE_PROFILE);
        });
        builder.addCase(updateUserProfile.rejected, (state, action) => {
            handleLoading(state);
            state.error = action.error;
        });
        builder.addCase(updateUserProfile.fulfilled, (state, action) => {
            state.profile = {
                ...state.profile,
                ...action.payload,
            };
            state.isSuccess = true;
            handleLoading(state);
        });
        builder.addCase(createSubscriptionOrder.pending, (state) => {
            handleLoading(state, UserFlow.SUBSCRIPTION);
        });
        builder.addCase(createSubscriptionOrder.rejected, (state) => {
            handleLoading(state);
        });
        builder.addCase(createSubscriptionOrder.fulfilled, (state) => {
            handleLoading(state);
        });
    },
});
export const { setProfile, setGuestUserId, updateProfile, resetUserProfile, setIsProfileModified, setAuthUser, resetAuthUser, setUserPermissions, setUserRoles, setExternalUserId, setInvalid, setIsUserAuthenticated, setIsUserAuthLoaded, setUserTapTrackingId, setIsLoading, setError, setIsSucceess, setFeatureFlags, setAuthenticationStatus, } = userSlice.actions;
export default userSlice.reducer;
